import { useRef, type MouseEvent, type PointerEvent } from 'react';

export type SwipeDirection = 'prev' | 'next';
export const SWIPE_MIN_PX = 40;

export interface SwipeHandlers {
  onPointerDown: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  onPointerCancel: () => void;
  onClickCapture: (event: MouseEvent) => void;
}

// A drag at least SWIPE_MIN_PX wide and wider than it is tall. Dragging right goes back, like turning a page. The click
// that ends a swipe is swallowed so it doesn't also press the day under the finger; the next pointerdown forgets it, so a
// swipe the browser ended without a click can't eat the next tap.
export function useSwipe(onSwipe: (direction: SwipeDirection) => void): SwipeHandlers {
  const origin = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  return {
    onPointerDown: (event) => {
      origin.current = { x: event.clientX, y: event.clientY };
      swiped.current = false;
    },
    onPointerUp: (event) => {
      const from = origin.current;
      origin.current = null;
      if (from === null) return;
      const dx = event.clientX - from.x;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(event.clientY - from.y)) return;
      swiped.current = true;
      onSwipe(dx > 0 ? 'prev' : 'next');
    },
    onPointerCancel: () => {
      origin.current = null;
    },
    onClickCapture: (event) => {
      if (!swiped.current) return;
      swiped.current = false;
      event.stopPropagation();
    },
  };
}
